/**
 * EdenDate i18n layer — 18 bundled languages for the web client.
 * Strings applied on top of the built-in English pack; missing keys fall back to English.
 */
import I18n from '@lib/langPack';

export type EdenLang = {
  code: string;
  label: string;   // native name
  flag: string;
};

export const EDEN_LANGS: EdenLang[] = [
  {code: 'en', label: 'English', flag: '🇺🇸'},
  {code: 'zh', label: '简体中文', flag: '🇨🇳'},
  {code: 'es', label: 'Español', flag: '🇪🇸'},
  {code: 'hi', label: 'हिन्दी', flag: '🇮🇳'},
  {code: 'ar', label: 'العربية', flag: '🇸🇦'},
  {code: 'pt', label: 'Português', flag: '🇧🇷'},
  {code: 'ru', label: 'Русский', flag: '🇷🇺'},
  {code: 'id', label: 'Indonesia', flag: '🇮🇩'},
  {code: 'fr', label: 'Français', flag: '🇫🇷'},
  {code: 'de', label: 'Deutsch', flag: '🇩🇪'},
  {code: 'ja', label: '日本語', flag: '🇯🇵'},
  {code: 'ko', label: '한국어', flag: '🇰🇷'},
  {code: 'tr', label: 'Türkçe', flag: '🇹🇷'},
  {code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳'},
  {code: 'th', label: 'ไทย', flag: '🇹🇭'},
  {code: 'ur', label: 'اردو', flag: '🇵🇰'},
  {code: 'ms', label: 'Melayu', flag: '🇲🇾'},
  {code: 'it', label: 'Italiano', flag: '🇮🇹'}
];

type Dict = Record<string, Record<string, string>>;

const T: Dict = {
  en: {
    'Login.Title': 'Sign in to EdenDate',
    'Login.StartText': 'Enter your phone number or email address\nto sign in to EdenDate.',
    'Login.Next': 'Next',
    'PleaseWait': 'Please wait...',
    'Login.PhoneLabel': 'Phone Number',
    'Login.PhoneLabelInvalid': 'Phone Number Invalid',
    'Login.Code.SentSms': 'We have sent you a code',
    'Login.Code.SentEmail': 'Please check your email and enter the code',
    'Login.EditPhone': 'Edit phone number',
    'Login.EditEmail': 'Change email address',
    'Country': 'Country',
    'Login.QR.Login': 'Log in by QR Code',
    'Login.QR.Title': 'Log in by QR Code',
    'Login.QR.Subtitle': 'Scan with EdenDate app on your phone',
    'Login.QR.Cancel': 'Log in by phone number'
  },
  zh: {
    'Login.Title': '登录 EdenDate',
    'Login.StartText': '输入手机号或邮箱地址\n登录 EdenDate。',
    'Login.Next': '下一步',
    'PleaseWait': '请稍候…',
    'Login.PhoneLabel': '手机号 / 邮箱',
    'Login.PhoneLabelInvalid': '号码格式不正确',
    'Login.Code.SentSms': '验证码已发送',
    'Login.Code.SentEmail': '请查收邮箱并输入验证码',
    'Login.EditPhone': '修改手机号',
    'Login.EditEmail': '修改邮箱地址',
    'Country': '国家/地区',
    'Login.QR.Login': '使用二维码登录',
    'Login.QR.Title': '二维码登录',
    'Login.QR.Subtitle': '用手机上的 EdenDate 扫描',
    'Login.QR.Cancel': '用手机号登录'
  },
  es: {
    'Login.Title': 'Inicia sesión en EdenDate',
    'Login.StartText': 'Introduce tu número de teléfono o correo\nelectrónico para entrar en EdenDate.',
    'Login.Next': 'Siguiente',
    'PleaseWait': 'Espera un momento…',
    'Login.PhoneLabel': 'Teléfono o correo',
    'Login.PhoneLabelInvalid': 'Número no válido',
    'Login.Code.SentSms': 'Te hemos enviado un código',
    'Login.Code.SentEmail': 'Revisa tu correo e introduce el código',
    'Login.EditPhone': 'Cambiar número',
    'Login.EditEmail': 'Cambiar correo',
    'Country': 'País',
    'Login.QR.Login': 'Iniciar sesión con código QR',
    'Login.QR.Title': 'Inicio con código QR',
    'Login.QR.Subtitle': 'Escanea con la app EdenDate',
    'Login.QR.Cancel': 'Entrar con el número'
  },
  hi: {
    'Login.Title': 'EdenDate में साइन इन करें',
    'Login.StartText': 'EdenDate में साइन इन करने के लिए\nफ़ोन नंबर या ईमेल डालें।',
    'Login.Next': 'आगे',
    'PleaseWait': 'कृपया प्रतीक्षा करें…',
    'Login.PhoneLabel': 'फ़ोन या ईमेल',
    'Login.PhoneLabelInvalid': 'नंबर ग़लत है',
    'Login.Code.SentSms': 'हमने आपको कोड भेज दिया है',
    'Login.Code.SentEmail': 'ईमेल देखें और कोड डालें',
    'Login.EditPhone': 'नंबर बदलें',
    'Login.EditEmail': 'ईमेल बदलें',
    'Country': 'देश',
    'Login.QR.Login': 'QR कोड से लॉग इन करें',
    'Login.QR.Title': 'QR कोड लॉगिन',
    'Login.QR.Subtitle': 'फ़ोन पर EdenDate से स्कैन करें',
    'Login.QR.Cancel': 'नंबर से लॉग इन करें'
  },
  ar: {
    'Login.Title': 'تسجيل الدخول إلى EdenDate',
    'Login.StartText': 'أدخل رقم هاتفك أو بريدك الإلكتروني\nلتسجيل الدخول إلى EdenDate.',
    'Login.Next': 'التالي',
    'PleaseWait': 'يرجى الانتظار…',
    'Login.PhoneLabel': 'الهاتف أو البريد',
    'Login.PhoneLabelInvalid': 'الرقم غير صحيح',
    'Login.Code.SentSms': 'أرسلنا إليك رمزًا',
    'Login.Code.SentEmail': 'تحقق من بريدك وأدخل الرمز',
    'Login.EditPhone': 'تغيير الرقم',
    'Login.EditEmail': 'تغيير البريد',
    'Country': 'الدولة',
    'Login.QR.Login': 'الدخول برمز QR',
    'Login.QR.Title': 'الدخول برمز QR',
    'Login.QR.Subtitle': 'امسح بتطبيق EdenDate',
    'Login.QR.Cancel': 'الدخول برقم الهاتف'
  },
  pt: {
    'Login.Title': 'Entrar no EdenDate',
    'Login.StartText': 'Informe o telefone ou e-mail\npara entrar no EdenDate.',
    'Login.Next': 'Continuar',
    'PleaseWait': 'Aguarde…',
    'Login.PhoneLabel': 'Telefone ou e-mail',
    'Login.PhoneLabelInvalid': 'Número inválido',
    'Login.Code.SentSms': 'Enviamos um código para você',
    'Login.Code.SentEmail': 'Verifique o e-mail e insira o código',
    'Login.EditPhone': 'Alterar telefone',
    'Login.EditEmail': 'Alterar e-mail',
    'Country': 'País',
    'Login.QR.Login': 'Entrar com QR Code',
    'Login.QR.Title': 'Login por QR Code',
    'Login.QR.Subtitle': 'Escaneie com o app EdenDate',
    'Login.QR.Cancel': 'Entrar com telefone'
  },
  ru: {
    'Login.Title': 'Вход в EdenDate',
    'Login.StartText': 'Введите номер телефона или эл. почту,\nчтобы войти в EdenDate.',
    'Login.Next': 'Далее',
    'PleaseWait': 'Подождите…',
    'Login.PhoneLabel': 'Телефон или почта',
    'Login.PhoneLabelInvalid': 'Неверный номер',
    'Login.Code.SentSms': 'Мы отправили код',
    'Login.Code.SentEmail': 'Проверьте почту и введите код',
    'Login.EditPhone': 'Изменить номер',
    'Login.EditEmail': 'Изменить почту',
    'Country': 'Страна',
    'Login.QR.Login': 'Войти по QR-коду',
    'Login.QR.Title': 'Вход по QR-коду',
    'Login.QR.Subtitle': 'Сканируйте в приложении EdenDate',
    'Login.QR.Cancel': 'Войти по номеру'
  },
  id: {
    'Login.Title': 'Masuk ke EdenDate',
    'Login.StartText': 'Masukkan nomor telepon atau email\nuntuk masuk ke EdenDate.',
    'Login.Next': 'Lanjut',
    'PleaseWait': 'Mohon tunggu…',
    'Login.PhoneLabel': 'Telepon atau email',
    'Login.PhoneLabelInvalid': 'Nomor tidak valid',
    'Login.Code.SentSms': 'Kode sudah kami kirim',
    'Login.Code.SentEmail': 'Periksa email lalu masukkan kode',
    'Login.EditPhone': 'Ubah nomor',
    'Login.EditEmail': 'Ubah email',
    'Country': 'Negara',
    'Login.QR.Login': 'Masuk dengan QR',
    'Login.QR.Title': 'Login QR',
    'Login.QR.Subtitle': 'Pindai dengan aplikasi EdenDate',
    'Login.QR.Cancel': 'Masuk dengan nomor'
  },
  fr: {
    'Login.Title': 'Se connecter à EdenDate',
    'Login.StartText': 'Saisissez votre numéro ou e-mail\npour vous connecter à EdenDate.',
    'Login.Next': 'Suivant',
    'PleaseWait': 'Patientez…',
    'Login.PhoneLabel': 'Téléphone ou e-mail',
    'Login.PhoneLabelInvalid': 'Numéro invalide',
    'Login.Code.SentSms': 'Nous vous avons envoyé un code',
    'Login.Code.SentEmail': 'Vérifiez votre e-mail et saisissez le code',
    'Login.EditPhone': 'Modifier le numéro',
    'Login.EditEmail': 'Modifier l’e-mail',
    'Country': 'Pays',
    'Login.QR.Login': 'Connexion par QR code',
    'Login.QR.Title': 'Connexion QR',
    'Login.QR.Subtitle': 'Scannez avec l’app EdenDate',
    'Login.QR.Cancel': 'Connexion par numéro'
  },
  de: {
    'Login.Title': 'Bei EdenDate anmelden',
    'Login.StartText': 'Telefonnummer oder E-Mail eingeben,\num sich anzumelden.',
    'Login.Next': 'Weiter',
    'PleaseWait': 'Bitte warten…',
    'Login.PhoneLabel': 'Telefon oder E-Mail',
    'Login.PhoneLabelInvalid': 'Ungültige Nummer',
    'Login.Code.SentSms': 'Wir haben dir einen Code gesendet',
    'Login.Code.SentEmail': 'Prüfe deine E-Mail und gib den Code ein',
    'Login.EditPhone': 'Nummer ändern',
    'Login.EditEmail': 'E-Mail ändern',
    'Country': 'Land',
    'Login.QR.Login': 'Mit QR-Code anmelden',
    'Login.QR.Title': 'QR-Anmeldung',
    'Login.QR.Subtitle': 'Mit der EdenDate-App scannen',
    'Login.QR.Cancel': 'Mit Nummer anmelden'
  },
  ja: {
    'Login.Title': 'EdenDate にログイン',
    'Login.StartText': '電話番号またはメールアドレスを入力して\nEdenDate にログイン。',
    'Login.Next': '次へ',
    'PleaseWait': '少々お待ちください…',
    'Login.PhoneLabel': '電話番号 / メール',
    'Login.PhoneLabelInvalid': '番号が正しくありません',
    'Login.Code.SentSms': '認証コードを送信しました',
    'Login.Code.SentEmail': 'メールを確認してコードを入力',
    'Login.EditPhone': '番号を変更',
    'Login.EditEmail': 'メールを変更',
    'Country': '国',
    'Login.QR.Login': 'QR コードでログイン',
    'Login.QR.Title': 'QR ログイン',
    'Login.QR.Subtitle': 'EdenDate アプリでスキャン',
    'Login.QR.Cancel': '電話番号でログイン'
  },
  ko: {
    'Login.Title': 'EdenDate 로그인',
    'Login.StartText': '전화번호 또는 이메일을 입력해\nEdenDate에 로그인하세요.',
    'Login.Next': '다음',
    'PleaseWait': '잠시만 기다려 주세요…',
    'Login.PhoneLabel': '전화번호 / 이메일',
    'Login.PhoneLabelInvalid': '번호가 올바르지 않습니다',
    'Login.Code.SentSms': '인증 코드를 보냈습니다',
    'Login.Code.SentEmail': '이메일을 확인하고 코드를 입력하세요',
    'Login.EditPhone': '번호 변경',
    'Login.EditEmail': '이메일 변경',
    'Country': '국가',
    'Login.QR.Login': 'QR 코드로 로그인',
    'Login.QR.Title': 'QR 로그인',
    'Login.QR.Subtitle': 'EdenDate 앱으로 스캔',
    'Login.QR.Cancel': '전화번호로 로그인'
  },
  tr: {
    'Login.Title': 'EdenDate’e giriş yap',
    'Login.StartText': 'Giriş yapmak için telefon numaranı\nveya e-postanı gir.',
    'Login.Next': 'İleri',
    'PleaseWait': 'Lütfen bekle…',
    'Login.PhoneLabel': 'Telefon veya e-posta',
    'Login.PhoneLabelInvalid': 'Numara geçersiz',
    'Login.Code.SentSms': 'Sana bir kod gönderdik',
    'Login.Code.SentEmail': 'E-postanı kontrol et ve kodu gir',
    'Login.EditPhone': 'Numarayı değiştir',
    'Login.EditEmail': 'E-postayı değiştir',
    'Country': 'Ülke',
    'Login.QR.Login': 'QR kod ile giriş',
    'Login.QR.Title': 'QR girişi',
    'Login.QR.Subtitle': 'EdenDate uygulamasıyla tara',
    'Login.QR.Cancel': 'Numarayla giriş'
  },
  vi: {
    'Login.Title': 'Đăng nhập EdenDate',
    'Login.StartText': 'Nhập số điện thoại hoặc email\nđể đăng nhập EdenDate.',
    'Login.Next': 'Tiếp tục',
    'PleaseWait': 'Vui lòng chờ…',
    'Login.PhoneLabel': 'SĐT hoặc email',
    'Login.PhoneLabelInvalid': 'Số không hợp lệ',
    'Login.Code.SentSms': 'Chúng tôi đã gửi mã',
    'Login.Code.SentEmail': 'Kiểm tra email và nhập mã',
    'Login.EditPhone': 'Đổi số điện thoại',
    'Login.EditEmail': 'Đổi email',
    'Country': 'Quốc gia',
    'Login.QR.Login': 'Đăng nhập bằng mã QR',
    'Login.QR.Title': 'Đăng nhập QR',
    'Login.QR.Subtitle': 'Quét bằng ứng dụng EdenDate',
    'Login.QR.Cancel': 'Đăng nhập bằng số'
  },
  th: {
    'Login.Title': 'เข้าสู่ระบบ EdenDate',
    'Login.StartText': 'กรอกเบอร์โทรหรืออีเมล\nเพื่อเข้าสู่ระบบ EdenDate',
    'Login.Next': 'ถัดไป',
    'PleaseWait': 'โปรดรอสักครู่…',
    'Login.PhoneLabel': 'เบอร์โทรหรืออีเมล',
    'Login.PhoneLabelInvalid': 'เบอร์ไม่ถูกต้อง',
    'Login.Code.SentSms': 'เราได้ส่งรหัสให้คุณแล้ว',
    'Login.Code.SentEmail': 'ตรวจอีเมลแล้วกรอกรหัส',
    'Login.EditPhone': 'เปลี่ยนเบอร์',
    'Login.EditEmail': 'เปลี่ยนอีเมล',
    'Country': 'ประเทศ',
    'Login.QR.Login': 'เข้าสู่ระบบด้วย QR',
    'Login.QR.Title': 'เข้าสู่ระบบ QR',
    'Login.QR.Subtitle': 'สแกนด้วยแอป EdenDate',
    'Login.QR.Cancel': 'เข้าสู่ระบบด้วยเบอร์'
  },
  ur: {
    'Login.Title': 'EdenDate میں سائن اِن کریں',
    'Login.StartText': 'سائن اِن کرنے کے لیے فون نمبر\nیا ای میل درج کریں۔',
    'Login.Next': 'آگے',
    'PleaseWait': 'براہِ کرم انتظار کریں…',
    'Login.PhoneLabel': 'فون یا ای میل',
    'Login.PhoneLabelInvalid': 'نمبر درست نہیں',
    'Login.Code.SentSms': 'ہم نے کوڈ بھیج دیا ہے',
    'Login.Code.SentEmail': 'ای میل دیکھ کر کوڈ درج کریں',
    'Login.EditPhone': 'نمبر تبدیل کریں',
    'Login.EditEmail': 'ای میل تبدیل کریں',
    'Country': 'ملک',
    'Login.QR.Login': 'QR کوڈ سے لاگ اِن',
    'Login.QR.Title': 'QR لاگ اِن',
    'Login.QR.Subtitle': 'EdenDate ایپ سے اسکین کریں',
    'Login.QR.Cancel': 'نمبر سے لاگ اِن'
  },
  ms: {
    'Login.Title': 'Log masuk EdenDate',
    'Login.StartText': 'Masukkan nombor telefon atau e-mel\nuntuk log masuk EdenDate.',
    'Login.Next': 'Seterusnya',
    'PleaseWait': 'Sila tunggu…',
    'Login.PhoneLabel': 'Telefon atau e-mel',
    'Login.PhoneLabelInvalid': 'Nombor tidak sah',
    'Login.Code.SentSms': 'Kod telah dihantar',
    'Login.Code.SentEmail': 'Semak e-mel dan masukkan kod',
    'Login.EditPhone': 'Tukar nombor',
    'Login.EditEmail': 'Tukar e-mel',
    'Country': 'Negara',
    'Login.QR.Login': 'Log masuk dengan QR',
    'Login.QR.Title': 'Log masuk QR',
    'Login.QR.Subtitle': 'Imbas dengan aplikasi EdenDate',
    'Login.QR.Cancel': 'Log masuk dengan nombor'
  },
  it: {
    'Login.Title': 'Accedi a EdenDate',
    'Login.StartText': 'Inserisci numero o email\nper accedere a EdenDate.',
    'Login.Next': 'Avanti',
    'PleaseWait': 'Attendi…',
    'Login.PhoneLabel': 'Telefono o email',
    'Login.PhoneLabelInvalid': 'Numero non valido',
    'Login.Code.SentSms': 'Ti abbiamo inviato un codice',
    'Login.Code.SentEmail': 'Controlla l’email e inserisci il codice',
    'Login.EditPhone': 'Modifica numero',
    'Login.EditEmail': 'Modifica email',
    'Country': 'Paese',
    'Login.QR.Login': 'Accedi con QR',
    'Login.QR.Title': 'Accesso QR',
    'Login.QR.Subtitle': 'Scansiona con l’app EdenDate',
    'Login.QR.Cancel': 'Accedi con il numero'
  }
};

const TABS: Record<string, {phone: string; email: string}> = {
  en: {phone: 'Phone', email: 'Email'},
  zh: {phone: '手机号', email: '邮箱'},
  es: {phone: 'Teléfono', email: 'Correo'},
  hi: {phone: 'फ़ोन', email: 'ईमेल'},
  ar: {phone: 'الهاتف', email: 'البريد'},
  pt: {phone: 'Telefone', email: 'E-mail'},
  ru: {phone: 'Телефон', email: 'Почта'},
  id: {phone: 'Telepon', email: 'Email'},
  fr: {phone: 'Téléphone', email: 'E-mail'},
  de: {phone: 'Telefon', email: 'E-Mail'},
  ja: {phone: '電話番号', email: 'メール'},
  ko: {phone: '전화번호', email: '이메일'},
  tr: {phone: 'Telefon', email: 'E-posta'},
  vi: {phone: 'Điện thoại', email: 'Email'},
  th: {phone: 'เบอร์โทร', email: 'อีเมล'},
  ur: {phone: 'فون', email: 'ای میل'},
  ms: {phone: 'Telefon', email: 'E-mel'},
  it: {phone: 'Telefono', email: 'Email'}
};

const CONTINUE_IN: Record<string, string> = {
  en: 'Continue in English',
  zh: '继续使用中文',
  es: 'Continuar en español',
  hi: 'हिन्दी में जारी रखें',
  ar: 'المتابعة بالعربية',
  pt: 'Continuar em português',
  ru: 'Продолжить на русском',
  id: 'Lanjutkan dalam Indonesia',
  fr: 'Continuer en français',
  de: 'Weiter auf Deutsch',
  ja: '日本語で続ける',
  ko: '한국어로 계속',
  tr: 'Türkçe devam et',
  vi: 'Tiếp tục bằng tiếng Việt',
  th: 'ดำเนินการต่อในภาษาไทย',
  ur: 'اردو میں جاری رکھیں',
  ms: 'Teruskan dalam Melayu',
  it: 'Continua in italiano'
};

const EMAIL_HINT: Record<string, string> = {
  en: 'If you don\'t see the code email, please check your spam folder.',
  zh: '如果收件箱没有看到验证码邮件，请检查垃圾邮件箱。',
  es: 'Si no ves el correo con el código, revisa la carpeta de spam.',
  hi: 'अगर कोड वाला ईमेल नहीं दिख रहा, तो स्पैम फ़ोल्डर देखें।',
  ar: 'إذا لم تجد رسالة الرمز، فتحقق من مجلد الرسائل غير المرغوب فيها.',
  pt: 'Se não encontrar o e-mail com o código, verifique a pasta de spam.',
  ru: 'Если письмо с кодом не пришло, проверьте папку «Спам».',
  id: 'Jika email kode tidak ada, periksa folder spam.',
  fr: 'Si vous ne voyez pas l\'e-mail avec le code, vérifiez vos spams.',
  de: 'Wenn die E-Mail mit dem Code fehlt, prüfe bitte den Spam-Ordner.',
  ja: '認証コードのメールが見当たらない場合は、迷惑メールフォルダをご確認ください。',
  ko: '인증 코드 메일이 보이지 않으면 스팸함을 확인해 주세요.',
  tr: 'Kod e-postasını göremiyorsanız lütfen spam klasörünü kontrol edin.',
  vi: 'Nếu không thấy email mã xác nhận, vui lòng kiểm tra thư mục spam.',
  th: 'หากไม่พบอีเมลรหัส โปรดตรวจสอบโฟลเดอร์สแปม',
  ur: 'اگر کوڈ والی ای میل نظر نہ آئے تو اسپیم فولڈر دیکھیں۔',
  ms: 'Jika e-mel kod tidak kelihatan, sila periksa folder spam.',
  it: 'Se non vedi l\'email con il codice, controlla la cartella spam.'
};

const COUNTRY_LANG: Record<string, string> = {
  CN: 'zh', HK: 'zh', TW: 'zh', SG: 'zh',
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', CL: 'es', VE: 'es', UY: 'es', EC: 'es', BO: 'es', PY: 'es', CR: 'es', PA: 'es', DO: 'es', GT: 'es', HN: 'es', SV: 'es', NI: 'es', CU: 'es', PR: 'es',
  IN: 'hi',
  SA: 'ar', AE: 'ar', EG: 'ar', QA: 'ar', KW: 'ar', BH: 'ar', OM: 'ar', JO: 'ar', LB: 'ar', IQ: 'ar', DZ: 'ar', MA: 'ar', TN: 'ar', LY: 'ar', SD: 'ar', YE: 'ar', SY: 'ar', PS: 'ar',
  BR: 'pt', PT: 'pt', AO: 'pt', MZ: 'pt', CV: 'pt', GW: 'pt', TL: 'pt',
  RU: 'ru', BY: 'ru', KZ: 'ru', KG: 'ru', AM: 'ru', AZ: 'ru', UZ: 'ru', TJ: 'ru', MD: 'ru',
  ID: 'id',
  FR: 'fr', BE: 'fr', LU: 'fr', MC: 'fr', SN: 'fr', CI: 'fr', CM: 'fr', ML: 'fr', BF: 'fr', NE: 'fr', TD: 'fr', GA: 'fr', CG: 'fr', CD: 'fr', MG: 'fr', HT: 'fr',
  DE: 'de', AT: 'de', LI: 'de',
  JP: 'ja',
  KR: 'ko',
  TR: 'tr',
  VN: 'vi',
  TH: 'th',
  PK: 'ur',
  MY: 'ms', BN: 'ms',
  IT: 'it', SM: 'it', VA: 'it', CH: 'de'
};

const GEO_FLAG = 'eden_geo_done';

/** Pick a default language from the visitor's IP country (runs once per browser). */
export async function autoDetectLang() {
  try {
    if(localStorage.getItem(STORAGE_KEY)) return;
    if(localStorage.getItem(GEO_FLAG)) return;
    localStorage.setItem(GEO_FLAG, '1');

    let code: string | undefined;

    // primary: ipapi.co (2.5s budget), fallback: ipwho.is
    for(const url of ['https://ipapi.co/json/', 'https://ipwho.is/']) {
      try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 2500);
        const res = await fetch(url, {signal: ctrl.signal});
        clearTimeout(timer);
        const data = await res.json();
        const cc = (data.country_code || data.country || '').toString().toUpperCase();
        if(cc) {
          code = COUNTRY_LANG[cc];
          break;
        }
      } catch(e) {}
    }

    if(!code) {
      const nav = (navigator.language || 'en').toLowerCase();
      const two = nav.split('-')[0];
      code = EDEN_LANGS.some((l) => l.code === two) ? two : 'en';
    }

    if(code && code !== getEdenLang()) {
      await applyEdenLang(code, true);
    }
  } catch(e) {}
}

const START_TEXT: Record<string, string> = {
  en: 'Enter your email address\nto sign in to EdenDate.',
  zh: '输入邮箱地址\n登录伊甸约。',
  es: 'Introduce tu correo electrónico\npara entrar en EdenDate.',
  hi: 'ईमेल पता दर्ज करें\nऔर EdenDate में साइन इन करें।',
  ar: 'أدخل بريدك الإلكتروني\nلتسجيل الدخول إلى EdenDate.',
  pt: 'Informe seu e-mail\npara entrar no EdenDate.',
  ru: 'Введите адрес эл. почты,\nчтобы войти в EdenDate.',
  id: 'Masukkan alamat email\nuntuk masuk ke EdenDate.',
  fr: 'Saisissez votre e-mail\npour vous connecter à EdenDate.',
  de: 'Gib deine E-Mail-Adresse ein,\num dich anzumelden.',
  ja: 'メールアドレスを入力して\nEdenDate にログイン。',
  ko: '이메일 주소를 입력해\nEdenDate에 로그인하세요.',
  tr: 'E-posta adresini gir\nve EdenDate’e giriş yap.',
  vi: 'Nhập địa chỉ email\nđể đăng nhập EdenDate.',
  th: 'กรอกอีเมล\nเพื่อเข้าสู่ระบบ EdenDate',
  ur: 'ای میل پتہ درج کریں\nاور EdenDate میں سائن اِن کریں۔',
  ms: 'Masukkan alamat e-mel\nuntuk log masuk EdenDate.',
  it: 'Inserisci il tuo indirizzo email\nper accedere a EdenDate.'
};

const packCache: Record<string, Record<string, string>> = {};

async function loadRemotePack(code: string): Promise<Record<string, string>> {
  if(code === 'en') return {};
  if(packCache[code]) return packCache[code];
  try {
    console.log('[edenLang] fetching pack', code);
    const res = await fetch(`/i18n/${code}.json`, {cache: 'force-cache'});
    console.log('[edenLang] pack status', code, res.status);
    if(res.ok) {
      packCache[code] = await res.json();
      return packCache[code];
    }
  } catch(e) {
    console.log('[edenLang] pack error', String(e));
  }
  return {};
}

export function openEdenLangSheet() {
  const existing = document.querySelector('.eden-lang-sheet');
  if(existing) { existing.remove(); return; }

  const wrap = document.createElement('div');
  wrap.className = 'eden-lang-sheet';

  const card = document.createElement('div');
  card.className = 'eden-lang-sheet-card';

  const title = document.createElement('div');
  title.className = 'eden-lang-sheet-title';
  title.textContent = 'Language';
  card.appendChild(title);

  const cur = getEdenLang();
  EDEN_LANGS.forEach((l) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'eden-lang-sheet-item' + (l.code === cur ? ' on' : '');
    btn.innerHTML = `<span class="flag">${l.flag}</span><span>${l.label}</span>`;
    btn.addEventListener('click', () => {
      wrap.remove();
      applyEdenLang(l.code, true).catch(() => {});
    });
    card.appendChild(btn);
  });

  wrap.appendChild(card);
  wrap.addEventListener('click', (e) => { if(e.target === wrap) wrap.remove(); });
  document.body.appendChild(wrap);
}

export function currentEdenLangLabel() {
  const l = EDEN_LANGS.find((x) => x.code === getEdenLang());
  return l ? l.label : 'English';
}

// High-visibility in-app strings (chat list / settings chrome) per language.
// These are merged on top of the English base pack.
const UI_PACKS: Record<string, Record<string, string>> = {
  zh: {
    'Search': '搜索',
    'Settings': '设置',
    'Contacts': '联系人',
    'SavedMessages': '收藏夹',
    'NewGroup': '新建群组',
    'NewChannel': '新建频道',
    'ArchivedChats': '已归档聊天',
    'Notifications': '通知',
    'Devices': '设备',
    'StickersName': '贴纸与表情',
    'Delete': '删除',
    'Cancel': '取消',
    'Done': '完成',
    'Save': '保存',
    'Apply': '应用',
    'Send': '发送',
    'SelectChat': '选择一个聊天',
    'Profile': '个人资料',
    'LogOut': '退出登录',
    'Archive': '归档',
    'Unarchive': '取消归档',
    'Reply': '回复',
    'Edit': '编辑',
    'Forward': '转发',
    'Copy': '复制',
    'OK': '确定',
    'Close': '关闭',
    'Yes': '是',
    'No': '否',
    'Online': '在线',
    'Next': '下一步',
    'AccountSettings.Language': '语言'
  },
  es: {
    'Search': 'Buscar', 'Settings': 'Ajustes', 'Contacts': 'Contactos', 'SavedMessages': 'Mensajes guardados',
    'NewGroup': 'Nuevo grupo', 'ArchivedChats': 'Chats archivados', 'Notifications': 'Notificaciones',
    'Devices': 'Dispositivos', 'Delete': 'Eliminar', 'Cancel': 'Cancelar', 'Done': 'Listo', 'Save': 'Guardar',
    'Send': 'Enviar', 'Profile': 'Perfil', 'LogOut': 'Cerrar sesión', 'Reply': 'Responder', 'Edit': 'Editar',
    'Forward': 'Reenviar', 'Copy': 'Copiar', 'OK': 'Aceptar', 'Close': 'Cerrar', 'Online': 'en línea',
    'AccountSettings.Language': 'Idioma'
  },
  ar: {
    'Search': 'بحث', 'Settings': 'الإعدادات', 'Contacts': 'جهات الاتصال', 'SavedMessages': 'الرسائل المحفوظة',
    'NewGroup': 'مجموعة جديدة', 'ArchivedChats': 'المحادثات المؤرشفة', 'Notifications': 'الإشعارات',
    'Devices': 'الأجهزة', 'Delete': 'حذف', 'Cancel': 'إلغاء', 'Done': 'تم', 'Save': 'حفظ', 'Send': 'إرسال',
    'Profile': 'الملف الشخصي', 'LogOut': 'تسجيل الخروج', 'Reply': 'رد', 'Edit': 'تعديل', 'Forward': 'إعادة توجيه',
    'Copy': 'نسخ', 'OK': 'حسنًا', 'Close': 'إغلاق', 'Online': 'متصل', 'AccountSettings.Language': 'اللغة'
  },
  ru: {
    'Search': 'Поиск', 'Settings': 'Настройки', 'Contacts': 'Контакты', 'SavedMessages': 'Избранное',
    'NewGroup': 'Новая группа', 'ArchivedChats': 'Архив', 'Notifications': 'Уведомления', 'Devices': 'Устройства',
    'Delete': 'Удалить', 'Cancel': 'Отмена', 'Done': 'Готово', 'Save': 'Сохранить', 'Send': 'Отправить',
    'Profile': 'Профиль', 'LogOut': 'Выйти', 'Reply': 'Ответить', 'Edit': 'Изменить', 'Forward': 'Переслать',
    'Copy': 'Копировать', 'OK': 'ОК', 'Close': 'Закрыть', 'Online': 'в сети', 'AccountSettings.Language': 'Язык'
  }
};

const GENERAL_SETTINGS: Record<string, string> = {
  en: 'General Settings',
  zh: '常规设置',
  es: 'Ajustes generales',
  hi: 'सामान्य सेटिंग्स',
  ar: 'الإعدادات العامة',
  pt: 'Configurações gerais',
  ru: 'Общие настройки',
  id: 'Pengaturan umum',
  fr: 'Paramètres généraux',
  de: 'Allgemeine Einstellungen',
  ja: '一般設定',
  ko: '일반 설정',
  tr: 'Genel ayarlar',
  vi: 'Cài đặt chung',
  th: 'การตั้งค่าทั่วไป',
  ur: 'عمومی ترتیبات',
  ms: 'Tetapan umum',
  it: 'Impostazioni generali'
};

const TR_LABELS: Record<string, Record<string, string>> = {
  en: {'Eden.Tr.From': 'Source language', 'Eden.Tr.To': 'Target language', 'Eden.Tr.AutoSend': 'Translate before sending', 'Eden.Tr.AutoRecv': 'Translate incoming'},
  zh: {'Eden.Tr.From': '源语言', 'Eden.Tr.To': '目标语言', 'Eden.Tr.AutoSend': '发送时自动翻译', 'Eden.Tr.AutoRecv': '接收时自动翻译'},
  es: {'Eden.Tr.From': 'Idioma de origen', 'Eden.Tr.To': 'Idioma de destino', 'Eden.Tr.AutoSend': 'Traducir al enviar', 'Eden.Tr.AutoRecv': 'Traducir al recibir'},
  ar: {'Eden.Tr.From': 'اللغة المصدر', 'Eden.Tr.To': 'اللغة الهدف', 'Eden.Tr.AutoSend': 'الترجمة قبل الإرسال', 'Eden.Tr.AutoRecv': 'ترجمة الوارد'},
  ru: {'Eden.Tr.From': 'Исходный язык', 'Eden.Tr.To': 'Целевой язык', 'Eden.Tr.AutoSend': 'Переводить при отправке', 'Eden.Tr.AutoRecv': 'Переводить входящие'},
  ja: {'Eden.Tr.From': '翻訳元', 'Eden.Tr.To': '翻訳先', 'Eden.Tr.AutoSend': '送信時に翻訳', 'Eden.Tr.AutoRecv': '受信時に翻訳'},
  ko: {'Eden.Tr.From': '원본 언어', 'Eden.Tr.To': '대상 언어', 'Eden.Tr.AutoSend': '보낼 때 번역', 'Eden.Tr.AutoRecv': '받을 때 번역'}
};


const WALLET_LABELS: Record<string, Record<string, string>> = {
  en: {
    'Eden.Balance': 'Balance', 'Eden.TopUp': 'Top up', 'Eden.Premium': 'Premium',
    'Eden.Close': 'Close', 'Eden.Premium.Desc': 'Become an EdenDate member',
    'Eden.Premium.Active': 'You are a member', 'Eden.Premium.Buy': 'Pay 100 U',
    'Eden.Premium.NoBalance': 'Not enough balance (100 U required)',
    'Eden.TopUp.Pending': 'TRC20 top-up is being connected. Please try later.',
    'Eden.Priv.FreePaidGroups': 'Join any paid group or channel for free',
    'Eden.Priv.DmStrangers': 'Message strangers directly',
    'Eden.Priv.Broadcast': 'Broadcast messages to everyone',
    'Eden.Priv.CreatePaidGroups': 'Create paid groups/channels and pull users',
    'Eden.DmBlocked.Title': 'Premium required',
    'Eden.DmBlocked': 'Only EdenDate members can message strangers. Upgrade to start a private chat.',
    'Eden.Rp.Title': 'Send red packet', 'Eden.Rp.Amount': 'Amount (U, min 1U)',
    'Eden.Rp.Count': 'Packets', 'Eden.Rp.Single': 'Single (full amount)',
    'Eden.Rp.Random': 'Lucky draw', 'Eden.Rp.Targeted': 'Targeted',
    'Eden.Rp.TargetUser': 'Target user id (optional)', 'Eden.Rp.Card': 'Open red packet',
    'Eden.Rp.Claim': 'Open', 'Eden.Send': 'Send', 'Eden.Save': 'Save',
    'Eden.PaidGroup': 'Paid group', 'Eden.PaidGroup.ChatId': 'Group/Channel id',
    'Eden.PaidGroup.Price': 'Join price (U)', 'Eden.PaidGroup.Hint': 'Members must pay this amount to join.',
    'Eden.PaidGroup.Saved': 'Saved. New members will be charged on join.',
    'Eden.PaidGroup.NeedPay': 'This group is paid. You need %s U in your balance to join.',
    'Eden.Pull.Title': 'Bulk pull users', 'Eden.Pull.ChatId': 'Group/Channel id',
    'Eden.Pull.All': 'All users', 'Eden.Pull.Some': 'Specific users',
    'Eden.Pull.Ids': 'User ids, comma separated', 'Eden.Pull.Run': 'Start',
    'Eden.Pull.Loading': 'Loading users…', 'Eden.Pull.None': 'No users to pull',
    'Eden.Pull.Running': 'Pulling', 'Eden.Pull.Done': 'Done',
    'Eden.Pull.NoChat': 'Please enter the group id', 'Eden.Pull.NoChatObj': 'Chat not found',
  },
  zh: {
    'Eden.Balance': '余额', 'Eden.TopUp': '充值', 'Eden.Premium': '高级版',
    'Eden.Close': '关闭', 'Eden.Premium.Desc': '成为伊甸园会员',
    'Eden.Premium.Active': '你已是会员', 'Eden.Premium.Buy': '支付 100 U',
    'Eden.Premium.NoBalance': '余额不足（需要 100 U）',
    'Eden.TopUp.Pending': 'TRC20 充值通道接入中，请稍后再试。',
    'Eden.Priv.FreePaidGroups': '免费进入付费群组和频道',
    'Eden.Priv.DmStrangers': '可以直接私聊陌生人',
    'Eden.Priv.Broadcast': '群发消息给所有人',
    'Eden.Priv.CreatePaidGroups': '建立付费群组/频道，一键拉取或强拉用户',
    'Eden.DmBlocked.Title': '需要高级版',
    'Eden.DmBlocked': '只有伊甸园会员才能私聊陌生人，升级后即可发起私聊。',
    'Eden.Rp.Title': '发送红包', 'Eden.Rp.Amount': '金额（U，最少 1U）',
    'Eden.Rp.Count': '红包个数', 'Eden.Rp.Single': '单人全额',
    'Eden.Rp.Random': '拼手气随机', 'Eden.Rp.Targeted': '定向红包',
    'Eden.Rp.TargetUser': '定向用户 ID（可选）', 'Eden.Rp.Card': '领取红包',
    'Eden.Rp.Claim': '领取', 'Eden.Send': '发送', 'Eden.Save': '保存',
    'Eden.PaidGroup': '付费群组', 'Eden.PaidGroup.ChatId': '群组/频道 ID',
    'Eden.PaidGroup.Price': '入群价格（U）', 'Eden.PaidGroup.Hint': '加入该群/频道需要支付该金额。',
    'Eden.PaidGroup.Saved': '已保存，新成员入群时会扣费。',
    'Eden.PaidGroup.NeedPay': '该群为付费群，入群需要支付 %s U，余额不足。',
    'Eden.Pull.Title': '批量拉人', 'Eden.Pull.ChatId': '群组/频道 ID',
    'Eden.Pull.All': '拉全部用户', 'Eden.Pull.Some': '拉指定用户',
    'Eden.Pull.Ids': '用户 ID，逗号分隔', 'Eden.Pull.Run': '开始',
    'Eden.Pull.Loading': '正在获取用户列表…', 'Eden.Pull.None': '没有可拉取的用户',
    'Eden.Pull.Running': '正在拉取', 'Eden.Pull.Done': '完成',
    'Eden.Pull.NoChat': '请填写群组/频道 ID', 'Eden.Pull.NoChatObj': '找不到该会话',
  },
  es: {
    'Eden.Balance': 'Saldo', 'Eden.TopUp': 'Recargar', 'Eden.Premium': 'Premium',
    'Eden.Close': 'Cerrar', 'Eden.Premium.Desc': 'Hazte miembro de EdenDate',
    'Eden.Premium.Active': 'Ya eres miembro', 'Eden.Premium.Buy': 'Pagar 100 U',
    'Eden.Premium.NoBalance': 'Saldo insuficiente (se requieren 100 U)',
    'Eden.TopUp.Pending': 'La recarga TRC20 se está conectando. Inténtalo más tarde.',
    'Eden.Priv.FreePaidGroups': 'Entra gratis a grupos y canales de pago',
    'Eden.Priv.DmStrangers': 'Escribe a desconocidos directamente',
    'Eden.Priv.Broadcast': 'Envía mensajes a todos',
    'Eden.Priv.CreatePaidGroups': 'Crea grupos/canales de pago y añade usuarios'
  },
  ar: {
    'Eden.Balance': 'الرصيد', 'Eden.TopUp': 'شحن', 'Eden.Premium': 'النسخة المميزة',
    'Eden.Close': 'إغلاق', 'Eden.Premium.Desc': 'كن عضوًا في EdenDate',
    'Eden.Premium.Active': 'أنت عضو بالفعل', 'Eden.Premium.Buy': 'ادفع 100 U',
    'Eden.Premium.NoBalance': 'الرصيد غير كافٍ (يلزم 100 U)',
    'Eden.TopUp.Pending': 'جارٍ ربط الشحن TRC20، حاول لاحقًا.',
    'Eden.Priv.FreePaidGroups': 'الدخول مجانًا إلى المجموعات والقنوات المدفوعة',
    'Eden.Priv.DmStrangers': 'مراسلة الغرباء مباشرة',
    'Eden.Priv.Broadcast': 'إرسال رسائل للجميع',
    'Eden.Priv.CreatePaidGroups': 'إنشاء مجموعات/قنوات مدفوعة وإضافة الأعضاء'
  },
  ru: {
    'Eden.Balance': 'Баланс', 'Eden.TopUp': 'Пополнить', 'Eden.Premium': 'Премиум',
    'Eden.Close': 'Закрыть', 'Eden.Premium.Desc': 'Станьте участником EdenDate',
    'Eden.Premium.Active': 'Вы уже участник', 'Eden.Premium.Buy': 'Оплатить 100 U',
    'Eden.Premium.NoBalance': 'Недостаточно средств (нужно 100 U)',
    'Eden.TopUp.Pending': 'Пополнение TRC20 подключается. Попробуйте позже.',
    'Eden.Priv.FreePaidGroups': 'Бесплатный вход в платные группы и каналы',
    'Eden.Priv.DmStrangers': 'Писать незнакомцам напрямую',
    'Eden.Priv.Broadcast': 'Рассылка сообщений всем',
    'Eden.Priv.CreatePaidGroups': 'Создавать платные группы/каналы и добавлять людей'
  },
  ja: {
    'Eden.Balance': '残高', 'Eden.TopUp': 'チャージ', 'Eden.Premium': 'プレミアム',
    'Eden.Close': '閉じる', 'Eden.Premium.Desc': 'EdenDate のメンバーになる',
    'Eden.Premium.Active': 'メンバーです', 'Eden.Premium.Buy': '100 U を支払う',
    'Eden.Premium.NoBalance': '残高不足（100 U 必要）',
    'Eden.TopUp.Pending': 'TRC20 チャージは接続中です。後でもう一度お試しください。',
    'Eden.Priv.FreePaidGroups': '有料グループ・チャンネルに無料で参加',
    'Eden.Priv.DmStrangers': '知らない人に直接メッセージ',
    'Eden.Priv.Broadcast': '全員にメッセージを一斉送信',
    'Eden.Priv.CreatePaidGroups': '有料グループ/チャンネルを作成してユーザーを追加'
  },
  ko: {
    'Eden.Balance': '잔액', 'Eden.TopUp': '충전', 'Eden.Premium': '프리미엄',
    'Eden.Close': '닫기', 'Eden.Premium.Desc': 'EdenDate 멤버가 되세요',
    'Eden.Premium.Active': '이미 멤버입니다', 'Eden.Premium.Buy': '100 U 결제',
    'Eden.Premium.NoBalance': '잔액 부족 (100 U 필요)',
    'Eden.TopUp.Pending': 'TRC20 충전 연결 중입니다. 나중에 다시 시도하세요.',
    'Eden.Priv.FreePaidGroups': '유료 그룹·채널 무료 입장',
    'Eden.Priv.DmStrangers': '낯선 사람에게 바로 메시지',
    'Eden.Priv.Broadcast': '모든 사람에게 메시지 발송',
    'Eden.Priv.CreatePaidGroups': '유료 그룹/채널 생성 및 사용자 초대'
  }
};

const STORAGE_KEY = 'eden_lang';


export function getEdenLang(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || 'en';
  } catch(e) {
    return 'en';
  }
}

export function applyEdenLangSoon(code: string) {
  applyEdenLang(code, false).catch(() => {});
  setTimeout(() => applyEdenLang(code, false).catch(() => {}), 900);
  setTimeout(() => applyEdenLang(code, false).catch(() => {}), 2400);
}

export async function applyEdenLang(code: string, reload = false) {
  const dict = T[code] || T.en;

  // persist first: the boot path and the lang-pack hook both read the stored value
  try {
    localStorage.setItem(STORAGE_KEY, code);
    document.documentElement.lang = code;
    document.documentElement.dir = (code === 'ar' || code === 'ur') ? 'rtl' : 'ltr';
  } catch(e) {}

  let base: Record<string, string> = {};
  try {
    base = (I18n as any).getCurrentStrings?.() || {};
  } catch(e) {}

  // keep everything else in English (the server has no lang packs)
  const merged = Object.assign({}, base, dict);
  if(dict['Login.PhoneLabel']) {
    merged['Contacts.PhoneNumber.Placeholder'] = dict['Login.PhoneLabel'];
  }
  const tabs = TABS[code] || TABS.en;
  merged['Eden.Phone'] = tabs.phone;
  merged['Eden.Email'] = tabs.email;

  const uiPack = UI_PACKS[code];
  if(uiPack) Object.assign(merged, uiPack);

  // server-side translated packs (280 core UI strings × 17 languages)
  const remote = await loadRemotePack(code);
  if(remote && Object.keys(remote).length) Object.assign(merged, remote);
  merged['Login.ContinueOnLanguage'] = CONTINUE_IN[code] || CONTINUE_IN.en;
  merged['Eden.EmailHint'] = EMAIL_HINT[code] || EMAIL_HINT.en;
  merged['Eden.GeneralSettings'] = GENERAL_SETTINGS[code] || GENERAL_SETTINGS.en;
  const trLabels = TR_LABELS[code];
  if(trLabels) Object.assign(merged, trLabels);

  const walletLabels = WALLET_LABELS[code] || WALLET_LABELS.en;
  Object.assign(merged, walletLabels);

  // email-only sign-in copy + the Chinese brand name
  merged['Login.StartText'] = START_TEXT[code] || START_TEXT.en;
  if(code === 'zh') {
    merged['Login.Title'] = '登录伊甸约';
  }

  try {
    (I18n as any).applyLocalLangPack(code, merged);
  } catch(e) {
    console.error('[edenLang] apply failed', e);
  }

  if(reload) {
    setTimeout(() => location.reload(), 60);
  }
}


// Register with the lang pack layer so any language change (login screen or
// in-app settings) also applies our bundled dictionary.
try {
  (I18n as any).setLocalLangApplier?.((code: string) => {
    const saved = getEdenLang();
    applyEdenLang(saved || code, false).catch(() => {});
  });
} catch(e) {}
